// Navigation channel — the bridge that lets the chat agent (and the
// chat's in-transcript affordances) drive page navigation without
// reaching into React router state directly.
//
// Agent tools are synchronous and live in lib/; they can't call the
// router's setSearchParams (a hook). So the navigation tool publishes a
// plain intent here, and App.jsx subscribes and applies it through its
// own mode-aware tab helpers (openExploreScopeExternal /
// openStudioItemExternal). This is the same tool→module-store→React
// pattern canvasStore / dockStore already use; it keeps the tool pure
// and unit-testable and keeps all URL mutation owned by App.
//
// Intent shape (see navIntent.js for the validated builders):
//   { kind: "explore", scopePath: [{dim, ids}], view?: string, cv?: string, label?: string }
//     (`view` lands on a specific lens, e.g. "scope.canvases"; `cv`
//      selects a canvas within it — the scope's default lens otherwise)
//   { kind: "canvas",  slug: string }
//     (open a saved/tailored canvas where the user already is: the
//      Dashboard home's ?cv= selection there, else the Canvases lens at
//      the explorer's current scope — App.openCanvasExternal decides)
//   { kind: "studio",  view: "kernels"|"canvases"|"workflows", itemId: string, label?: string }
//   { kind: "trace",   traceId: string, reading?: "lanes"|..., graph?: string, claim?: string, expand?: boolean }
//     (open a conversation as a trace tab on a reading; `graph` names the
//      chat whose knowledge graph the trace readings wear, `claim` one of
//      its claims to arrive on -- see traceGraphOverlay.js)
//
// ChatConsole ALSO subscribes — to close the overlay after a navigation
// when the chat is floating (not docked), so the user actually sees the
// page it opened. A docked chat stays put beside the new tab.

const subscribers = new Set();
let lastIntent = null;

export function requestNavigation(intent) {
  if (!intent || typeof intent !== "object") return;
  lastIntent = intent;
  for (const cb of subscribers) {
    try {
      cb(intent);
    } catch (err) {
      // A broken subscriber must not stop the others (or the tool that
      // fired this) — log and move on.
      // eslint-disable-next-line no-console
      console.warn("[navigation] subscriber threw:", err);
    }
  }
}

export function subscribeNavigation(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export function getLastNavigation() {
  return lastIntent;
}

export function resetNavigationIdentityState() {
  lastIntent = null;
}
